import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, TableCell, TableRow } from '@material-ui/core';
import { ActionLink, Block, Heading, Tooltip } from '@klp/design';
import type { DisplayData } from 'mui-datatables';
import {
    type MUIDataTableColumn,
    type MUIDataTableOptions,
} from 'mui-datatables';
import { IconName, Illustration } from '@klp/design/Illustrations';
import {
    prosjektProcessApiName,
    type ProsjektAvfall,
} from '../services/rtkApi';
import { FileDownload } from '@klp/filedownload/index';
import { generateApiRoute } from '@klp/core/api/helpers';
import { ProsjektAvfallExpandable } from '../components/ProsjektAvfallExpandable';
import { NumberFormatted } from '@klp/formgenerator/components/NumberFormatted';
import { ProsjektRoutes } from '@klp/prosjekt/config';
import { PersistentFilterTable } from '@klp/design/PersistentFilter/PersistentFilterTable';
import { ProsjektAvfallSummary } from './ProsjektAvfallSummary';
import { ProsjektAvfallKlimagassForklaring } from './ProsjektAvfallKlimagassForklaring';
import { deepDiff } from '@klp/utils/deepDiff';

type ProsjektAvfallTableProps = {
    prosjektAvfall: Array<ProsjektAvfall>;
};

export function ProsjektAvfallTable({
    prosjektAvfall,
}: ProsjektAvfallTableProps) {
    const { t } = useTranslation();
    const [summaryFilteredAvfall, setSummaryFilteredAvfall] =
        useState(prosjektAvfall);

    const tableData = useMemo(
        () =>
            prosjektAvfall.map((pa) => ({
                ...pa,
                sorteringsgrad:
                    pa.totalMengde > 0
                        ? (pa.totalMengdeSortert / pa.totalMengde) * 100
                        : 0,
                // Text, not the boolean, so filter, sort and CSV export show Ja/Nei/–
                klimagassRelevantTekst:
                    pa.klimagassberegningRelevant === null
                        ? '–'
                        : t(
                              pa.klimagassberegningRelevant
                                  ? 'sustainability.prosjektAvfall.relevantJa'
                                  : 'sustainability.prosjektAvfall.relevantNei',
                          ),
            })),
        [prosjektAvfall, t],
    );

    const tableColumns: Array<MUIDataTableColumn> = [
        {
            label: t('sustainability.prosjektAvfall.prosjektnummer'),
            name: 'prosjektnummer',
            options: {
                filter: false,
                customBodyRenderLite: (dataIndex) => (
                    <ActionLink
                        actionType="link"
                        href={ProsjektRoutes.projectSummary(
                            prosjektAvfall[dataIndex].prosessInstansId,
                            prosjektAvfall[dataIndex].prosjektId,
                        )}
                        target="_blank"
                    >
                        {prosjektAvfall[dataIndex].prosjektnummer}
                    </ActionLink>
                ),
            },
        },
        {
            label: t('sustainability.prosjektAvfall.prosjekttype'),
            name: 'prosjektType',
            options: { filter: true },
        },
        {
            label: t('sustainability.prosjektAvfall.prosjektnavn'),
            name: 'prosjektnavn',
            options: { filter: false },
        },
        {
            label: t('sustainability.prosjektAvfall.region'),
            name: 'region',
            options: { display: false, filter: true },
        },
        {
            label: t('sustainability.prosjektAvfall.byggtype'),
            name: 'byggtype',
            options: { display: false, filter: true },
        },
        {
            label: t('sustainability.prosjektAvfall.bygg'),
            name: 'byggnavn',
            options: { filter: false },
        },
        {
            label: t('sustainability.prosjektAvfall.prosjektleder'),
            name: 'prosjektleder',
            options: { filter: false },
        },
        {
            label:
                t('sustainability.prosjektAvfall.klimagassberegning') +
                ' (kg CO2ekv/m2)',
            name: 'klimagassberegning',
            options: {
                filter: false,
                customBodyRender: (value) => <NumberFormatted value={value} />,
                setCellProps: () => ({ style: { textAlign: 'center' } }),
                setCellHeaderProps: () => ({ style: { maxWidth: '225px' } }),
            },
        },
        {
            label: t('sustainability.prosjektAvfall.klimagassRelevant'),
            name: 'klimagassRelevantTekst',
            options: {
                filter: true,
                hint: t('sustainability.prosjektAvfall.klimagassRelevantHint'),
                setCellProps: () => ({ style: { textAlign: 'center' } }),
            },
        },
        {
            label: t('sustainability.prosjektAvfall.klimagassForklaring'),
            name: 'klimagassberegningForklaring',
            options: {
                filter: false,
                customBodyRenderLite: (dataIndex) => (
                    <ProsjektAvfallKlimagassForklaring
                        forklaring={
                            prosjektAvfall[dataIndex]
                                .klimagassberegningForklaring
                        }
                        prosjektnummer={
                            prosjektAvfall[dataIndex].prosjektnummer
                        }
                        prosjektnavn={prosjektAvfall[dataIndex].prosjektnavn}
                    />
                ),
                setCellProps: () => ({ style: { maxWidth: '260px' } }),
            },
        },
        {
            label: t('sustainability.prosjektAvfall.prosjektSum') + ' (kg)',
            name: 'totalMengde',
            options: {
                filter: false,
                customBodyRender: (value) => <NumberFormatted value={value} />,
                setCellProps: () => ({ style: { textAlign: 'center' } }),
            },
        },
        {
            label: t('sustainability.prosjektAvfall.sorteringsgrad') + ' (%)',
            name: 'sorteringsgrad',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: number) => (
                    <NumberFormatted
                        value={value > 0 ? value.toFixed(1) : value}
                    />
                ),
                setCellProps: () => ({ style: { textAlign: 'center' } }),
            },
        },
        {
            label: t('sustainability.prosjektAvfall.document'),
            name: 'avfallDokumentId',
            options: {
                filter: false,
                customBodyRender: (value: number | null) =>
                    value !== null && (
                        <Block>
                            <IconButton color="primary">
                                <FileDownload
                                    path={generateApiRoute([
                                        prosjektProcessApiName,
                                        'vedlegg',
                                        value,
                                    ])}
                                    name={'vedlegg-' + value + '.pdf'}
                                    preview={true}
                                >
                                    <Tooltip translation="sustainability.prosjektAvfall.documentTooltip">
                                        <Illustration
                                            icon={IconName.SEARCH}
                                            klpPalette="primary"
                                            klpColor="main"
                                        />
                                    </Tooltip>
                                </FileDownload>
                            </IconButton>
                        </Block>
                    ),
            },
        },
    ];

    const handleDisplayDataChange = (data?: DisplayData) => {
        const displayedRows =
            data?.map((row) => prosjektAvfall[row.dataIndex]) ?? [];

        const diff = deepDiff(displayedRows, summaryFilteredAvfall);
        if (!diff) return;

        setSummaryFilteredAvfall(displayedRows);
    };

    const tableOptions: MUIDataTableOptions = {
        search: true,
        filter: true,
        download: true,
        print: false,
        viewColumns: true,
        sort: true,
        selectableRows: 'none',
        expandableRows: true,
        expandableRowsHeader: false,
        expandableRowsOnClick: false,
        isRowExpandable: (dataIndex, _expandedRows) => {
            const element = prosjektAvfall[dataIndex];
            return !!element.id && !!element.avfallDokumentId;
        },
        renderExpandableRow: (rowData, rowMeta) => {
            const colSpan = rowData.length + 1;
            return (
                <TableRow>
                    <TableCell colSpan={colSpan}>
                        <ProsjektAvfallExpandable
                            element={prosjektAvfall[rowMeta.dataIndex]}
                        />
                    </TableCell>
                </TableRow>
            );
        },
        setFilterChipProps: () => {
            return {
                color: 'secondary',
                variant: 'outlined',
            };
        },
        onTableInit(_action, tableState) {
            handleDisplayDataChange(tableState.displayData);
        },
        onTableChange(_action, tableState) {
            handleDisplayDataChange(tableState.displayData);
        },
        textLabels: {
            body: {
                noMatch: t('sustainability.prosjektAvfall.emptyTable'),
                toolTip: t('tables.body.toolTip'),
                columnHeaderTooltip: (column) =>
                    t('tables.body.toolTipFor') + column.label,
            },
            pagination: {
                next: t('tables.pagination.next'),
                previous: t('tables.pagination.previous'),
                rowsPerPage: t('tables.pagination.rowsPerPage'),
                displayRows: t('tables.pagination.displayRows'),
            },
            filter: {
                all: t('tables.filter.all'),
                title: t('tables.filter.title'),
                reset: t('tables.filter.reset'),
            },
            toolbar: {
                search: t('tables.toolbar.search'),
                downloadCsv: t('tables.toolbar.downloadCsv'),
                print: t('tables.toolbar.print'),
                viewColumns: t('tables.toolbar.viewColumns'),
                filterTable: t('tables.toolbar.filterTable'),
            },
            viewColumns: {
                title: t('tables.viewColumns.title'),
                titleAria: t('tables.viewColumns.titleAria'),
            },
        },
    };

    const tableTitle = (
        <Heading klpHeadingLevel="THREE" klpColor="dark">
            {t('sustainability.prosjektAvfall.tableTitle')}
        </Heading>
    );

    return (
        <>
            <ProsjektAvfallSummary prosjektAvfall={summaryFilteredAvfall} />
            <PersistentFilterTable
                tableKey="prosjektAvfall"
                title={tableTitle}
                data={tableData}
                columns={tableColumns}
                options={tableOptions}
            />
        </>
    );
}
